import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { 
  Clock, 
  Calendar, 
  CreditCard, 
  Loader2, 
  AlertCircle, 
  User,
  Timer
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const DEFAULT_PHOTO = "https://images.unsplash.com/photo-1629121699638-976764fb4c8c?w=600&h=800&fit=crop";

export default function SuggestionPayment() {
  const { suggestionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("usd");
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    fetchSuggestion();
  }, [suggestionId]);

  // Countdown timer
  useEffect(() => {
    if (!suggestion || suggestion.status !== "pending") return;
    
    const updateTimer = () => {
      const expiresAt = new Date(suggestion.expires_at);
      const now = new Date();
      const diff = expiresAt - now;
      
      if (diff <= 0) {
        setTimeRemaining(null);
        setSuggestion(prev => ({ ...prev, status: "expired" }));
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining({ hours, minutes, seconds });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [suggestion]);

  const fetchSuggestion = async () => {
    try {
      const response = await axios.get(`${API}/suggestion/${suggestionId}`);
      setSuggestion(response.data);
    } catch (error) {
      console.error("Error fetching suggestion:", error);
      toast.error("Sugerencia no encontrada o expirada");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para continuar");
      const returnUrl = `/pagar-sugerencia/${suggestionId}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    if (!consentAccepted) {
      toast.error("Debes aceptar el consentimiento");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/suggestion/${suggestionId}/checkout`, {
        selected_currency: selectedCurrency,
        consent_accepted: consentAccepted,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No se recibió URL de pago");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      const errorMsg = error.response?.data?.detail || "Error al procesar el pago";
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!suggestion) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-600">Sugerencia no encontrada</p>
          </div>
        </div>
      </Layout>
    );
  }

  const isExpired = suggestion.status === "expired";
  const isPaid = suggestion.status === "paid";
  const isPending = suggestion.status === "pending";

  return (
    <Layout>
      <div className="min-h-screen bg-stone-50 py-8">
        <div className="container-main max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              Invitación a sesión
            </h1>
            <p className="text-stone-600 mt-2">
              {suggestion.listener_name} te ha sugerido una sesión
            </p>
          </div>

          {/* Status Banner */}
          {isExpired && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="expired-banner">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Esta invitación ha expirado</p>
                <p className="text-sm text-red-600">Contacta a tu listener para solicitar una nueva invitación</p>
              </div>
            </div>
          )}

          {isPaid && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="paid-banner">
              <CreditCard className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">Sesión agendada exitosamente</p>
                <p className="text-sm text-green-600">Revisa tu email para los detalles de la sesión</p>
              </div>
            </div>
          )}

          {/* Timer */}
          {isPending && timeRemaining && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="timer-banner">
              <div className="flex items-center justify-center gap-2 text-amber-800">
                <Timer className="w-5 h-5" />
                <span className="font-medium">Expira en:</span>
                <span className="font-mono text-lg font-bold">
                  {String(timeRemaining.hours).padStart(2, '0')}:
                  {String(timeRemaining.minutes).padStart(2, '0')}:
                  {String(timeRemaining.seconds).padStart(2, '0')}
                </span>
              </div>
            </div>
          )}

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-stone-100 overflow-hidden">
            {/* Listener Info */}
            <div className="p-6 border-b border-stone-100">
              <div className="flex items-center gap-4">
                <img
                  src={getFullUrl(suggestion.listener_photo) || DEFAULT_PHOTO}
                  alt={suggestion.listener_name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-stone-100"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = DEFAULT_PHOTO;
                  }}
                />
                <div>
                  <h2 className="font-semibold text-lg text-foreground">{suggestion.listener_name}</h2>
                  <p className="text-stone-500 text-sm">@{suggestion.listener_username}</p>
                </div>
              </div>
              
              {suggestion.message && (
                <div className="mt-4 bg-stone-50 rounded-lg p-4 border-l-4 border-primary">
                  <p className="text-stone-600 italic">"{suggestion.message}"</p>
                </div>
              )}
            </div>

            {/* Session Details */}
            <div className="p-6 bg-primary/5">
              <h3 className="font-medium text-foreground mb-4">Detalles de la sesión</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="text-foreground">
                    {format(parseISO(suggestion.suggested_date), "EEEE d 'de' MMMM, yyyy", { locale: es })}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="text-foreground">{suggestion.suggested_time} hrs</span>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-primary" />
                  <span className="text-foreground">Sesión de 50 minutos por videollamada</span>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="p-6 border-t border-stone-100">
              <div className="text-center mb-4">
                <span className="text-3xl font-bold text-primary">
                  ${selectedCurrency === "mxn" ? suggestion.price_mxn : suggestion.price_usd}
                </span>
                <span className="text-stone-500 ml-1 uppercase">{selectedCurrency}</span>
              </div>
              
              {/* Currency Selector */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setSelectedCurrency("usd")}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    selectedCurrency === "usd"
                      ? "bg-primary text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                  data-testid="currency-usd-btn"
                >
                  USD
                </button>
                <button
                  onClick={() => setSelectedCurrency("mxn")}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    selectedCurrency === "mxn"
                      ? "bg-primary text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                  data-testid="currency-mxn-btn"
                >
                  MXN
                </button>
              </div>
              
              <p className="text-xs text-stone-400 text-center">
                {selectedCurrency === "mxn" 
                  ? `≈ $${suggestion.price_usd} USD`
                  : `≈ $${suggestion.price_mxn} MXN`
                }
              </p>
            </div>

            {/* Consent & Payment */}
            {isPending && (
              <div className="p-6 border-t border-stone-100 space-y-4">
                {/* Legal disclaimer */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Important:</strong> Apoyum Listeners are NOT licensed therapists or mental health professionals. 
                    This service is for emotional support only, not professional therapy. Listeners may be located outside the U.S.
                  </p>
                </div>

                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                  <Checkbox
                    id="consent"
                    checked={consentAccepted}
                    onCheckedChange={setConsentAccepted}
                    className="mt-1"
                    data-testid="consent-checkbox"
                  />
                  <label htmlFor="consent" className="text-sm text-stone-600 leading-relaxed cursor-pointer">
                    I understand and accept that this is an emotional support service, NOT professional therapy. 
                    Listeners are NOT licensed mental health professionals.
                  </label>
                </div>

                <Button
                  className="w-full py-6 text-lg"
                  onClick={handlePayment}
                  disabled={!consentAccepted || processing}
                  data-testid="pay-suggestion-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pagar ${selectedCurrency === "mxn" ? suggestion.price_mxn : suggestion.price_usd} {selectedCurrency.toUpperCase()}
                    </>
                  )}
                </Button>

                <p className="text-xs text-stone-500 text-center">{LEGAL_DISCLAIMER}</p>
              </div>
            )}

            {/* If expired or paid, show CTA to go back */}
            {(isExpired || isPaid) && (
              <div className="p-6 border-t border-stone-100">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate(isPaid ? "/mis-sesiones" : "/")}
                  data-testid="cta-btn"
                >
                  {isPaid ? "Ver mis sesiones" : "Volver al inicio"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
